// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { FormControlLabel, InputAdornment, Paper, TextField, TextFieldProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import merge from 'lodash/merge';

import Switch from '@shared/components/Switch';
import ProfileInfo from '@shared/components/ProfileInfo';
import Button, { ButtonProps } from '@shared/components/Button';
import Filters, { FiltersProps } from './components/Filters';
import BackButton, { BackButtonProps } from '@shared/components/BackButton';
import Ellipsis from '@shared/components/Ellipsis';
import SearchIcon from '@shared/icons/Search';
import { getTestAttr, getQueries, setQueries } from '@shared/utils/common';
import SystemUser from '@shared/models/system-user';
import { ListRequestParams, Filtering } from '@shared/types/services';
import { RecursivePartial } from '@shared/types/common';
import container from '@core/di';
import AuthStore from '@core/stores/auth';
import { ActivityFilter } from '@shared/constants/services';

import { observer } from 'mobx-react';

export interface HeaderConfig {
  moduleName: string;
  moduleTestName?: string;
  activeItemsToggle?: boolean;
  filters?: Omit<FiltersProps, 'search'>;
  addButton?: Partial<ButtonProps>;
  backButton?: Partial<BackButtonProps>;
  textField?: TextFieldProps;
  onSearch?: () => any;
}

export interface HeaderProps extends HeaderConfig {
  className?: string;
  user: SystemUser;
  onLogoutClick: () => any;
}

// TBD: need to walk through and comment this component.
// Apparently it is used to configure the header (page title, and on some pages "back" button, "Search" box, ...)
@observer class Header extends React.Component<HeaderProps> {
  private authStore = container.get<AuthStore>(AuthStore.diToken);

  readonly state = {
    searchKeyword: this.queries.filtering?.searchKeyword || ''
  };

  private get queries(): ListRequestParams {
    return getQueries() as unknown as ListRequestParams;
  }

  componentDidUpdate(prevProps: HeaderProps) {
    if (prevProps.moduleName && prevProps.moduleName !== this.props.moduleName) {
      this.handleModuleChange();
    }
  }

  private handleModuleChange = () => {
    this.setState(
      (state) => ({ ...state, searchKeyword: this.queries.filtering?.searchKeyword || '' })
    );
  }

  private handleSearchChange = (e) => {
    e.persist();

    this.setState((state) => ({ ...state, searchKeyword: e.target.value }));
  }

  private getList = (filtering?: Filtering) => {
    const queriesToUpdate: RecursivePartial<ListRequestParams> = {
      filtering,
      pagination: {
        page: 0,
      },
    };
    const futureQueries = merge(
      this.queries,
      queriesToUpdate,
    );

    setQueries(futureQueries);

    const { onSearch } = this.props;

    if (onSearch) {
      onSearch();
    }
  }

  private handleShowActiveChange = () => {
    const nextValue = {
      [ActivityFilter.active]: ActivityFilter.all,
      [ActivityFilter.all]: ActivityFilter.active
    };

    this.getList({ activityFilter: nextValue[this.activityFilterValue] });
  }

  private handleSearchSubmit = (e) => {
    e.preventDefault();

    this.getList({
      searchKeyword: this.state.searchKeyword,
    });
  }

  private handleSearchReset = () => {
    this.setState(
      (state) => ({ ...state, searchKeyword: '' }),
      () => this.getList({ searchKeyword: '' })
    );
  }

  private get activityFilterValue() {
    const { filtering } = this.queries;

    return filtering?.activityFilter || ActivityFilter.all;
  }

  render() {
    const {
      addButton,
      backButton,
      moduleName,
      textField,
      user,
      moduleTestName,
      filters,
      activeItemsToggle,
      onLogoutClick
    } = this.props;
    const classes = withStyles.getClasses(this.props);
    const { searchKeyword } = this.state;
    const { filtering } = this.queries;
    const showActive = this.activityFilterValue === ActivityFilter.active;
    const { isProjectManager } = this.authStore.user;

    return (
      <Paper className={classes.root}>
        <section>
          {backButton && <BackButton classes={{ root: classes.backBtn }} {...backButton} />}
          <Ellipsis maxWidth="40vw" classes={{ root: classes.moduleName }} text={moduleName} />
          {(addButton?.text || addButton?.children) && (
            <Button
              {...addButton}
              variant="outlined"
              {...getTestAttr(moduleTestName || moduleName, 'add-header', 'button')}
              size="small"
            />
          )}
        </section>
        <section>
          {textField && (
            <form onSubmit={this.handleSearchSubmit} className={classes.searchForm}>
              <TextField
                margin="dense"
                variant="standard"
                className={classes.searchTextField}
                value={searchKeyword}
                onChange={this.handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon className={classes.searchTextIconLook} />
                    </InputAdornment>
                  ),
                  endAdornment: filtering?.searchKeyword && (
                    <InputAdornment position="end">
                      <CloseIcon className={classes.searchTextIconReset} onClick={this.handleSearchReset} />
                    </InputAdornment>
                  ),
                  classes: { input: classes.searchTextInput }
                }}
                inputProps={getTestAttr(moduleName, 'header-search', 'input')}
                {...textField}
              />
            </form>
          )}
          {filters && (
            <Filters
              {...filters}
              moduleName={moduleName}
              classes={{
                rootBtn: classes.filtersBtn
              }}
              search={this.getList}
            />
          )}
          {isProjectManager && activeItemsToggle && (
            <FormControlLabel
              classes={{
                root: classes.showActiveWrapper,
                label: classes.showActiveLabel,
              }}
              labelPlacement="start"
              control={(
                <Switch
                  disableRipple
                  testAttributeArgs={{
                    module: moduleName,
                    elementDescription: 'active'
                  }}
                  checked={showActive}
                  onChange={this.handleShowActiveChange}
                />
              )}
              label="Show only active"
            />
          )}
          {(filters || textField || activeItemsToggle) && <div className={classes.divider} />}
          <ProfileInfo
            user={user}
            onLogoutClick={onLogoutClick}
          />
        </section>
      </Paper>
    );
  }
}

const HeaderStyled = withStyles(
  Header, 
  (theme, props) => ({
    "root": {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0, 8),
      height: 80,

      '& section': {
        display: 'flex',
        alignItems: 'center'
      }
    },
    "moduleName": {
      padding: theme.spacing(6),
      // marginRight: spacing(6),
      fontSize: 27,
      color: theme.palette.colors.black,
      fontWeight: 'bold'
    },
    "backBtn": {
      margin: theme.spacing(0, 8, 0, 0)
    },
    "searchForm": {},
    "searchTextField": {
      width: 280,
    },
    "searchTextInput": {
      fontSize: 14,
      '&::placeholder': {
        fontSize: 14,
        color: theme.palette.colors.grey,
      },
    },
    "searchTextIconLook": {
      stroke: theme.palette.colors.greyLight3,
      width: 16,
      height: 16,
      marginBottom: theme.spacing()
    },
    "searchTextIconReset": {
      cursor: 'pointer',
      color: theme.palette.colors.greyDark,
      fontSize: 20
    },
    "filtersBtn": {
      marginLeft: theme.spacing(6),
    },
    "showActiveWrapper": {
      margin: theme.spacing(0, 0, 0, 6),
      alignItems: 'center',
    },
    "showActiveLabel": {
      fontWeight: 500,
      fontSize: 12,
      color: theme.palette.colors.greyDark,
      marginRight: theme.spacing(2),
    },
    "divider": {
      width: 2,
      margin: theme.spacing(0, 4, 0, 6),
      borderRadius: '4px',
      height: 18,
      background: theme.palette.colors.greyLight,
    }
  })
);

export default HeaderStyled;